/**
 * The sort a table is showing, held for it.
 *
 * `DataTable` reports a press and draws whatever it is given, so the state
 * lives with the caller. Every list would otherwise repeat the same
 * `useState` pair, and a callback rebuilt each render would rebuild the table
 * with it.
 *
 * @module components/ui/useTableSort
 */

import { useCallback } from "react";

import type { SortState } from "./DataTable";
import { useStoredState } from "./useStoredState";

export interface TableSort {
  sort: SortState;
  onSortChange: (next: SortState) => void;
}

function readSort(stored: unknown): SortState | undefined {
  if (typeof stored !== "object" || stored === null) {
    return undefined;
  }
  const { key, direction } = stored as Record<string, unknown>;
  return typeof key === "string" && (direction === "asc" || direction === "desc")
    ? { key, direction }
    : undefined;
}

/**
 * @param initial - The column and direction the table opens on.
 * @param storageKey - Remembers the sort across visits; shares the list's own key.
 * @returns The pair `DataTable` takes.
 * @example
 * ```tsx
 * const sort = useTableSort({ key: "title", direction: "asc" });
 * <CollectionView {...sort} columns={columns} rows={rows} … />
 * ```
 */
export function useTableSort(initial: SortState, storageKey?: string): TableSort {
  const [sort, setSort] = useStoredState(
    storageKey === undefined ? undefined : `sqSort_${storageKey}`,
    initial,
    readSort,
  );
  const onSortChange = useCallback(
    (next: SortState) => {
      setSort(next);
    },
    [setSort],
  );

  return { sort, onSortChange };
}
